import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TerminalIcon from '@mui/icons-material/Terminal';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import BackupIcon from '@mui/icons-material/Backup';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import TuneIcon from '@mui/icons-material/Tune';
import PeopleIcon from '@mui/icons-material/People';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryIcon from '@mui/icons-material/History';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import LinkIcon from '@mui/icons-material/Link';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchGames,
  controlGame,
  createBackup,
  fetchSavegameDownloadToken,
  triggerSavegameDownload,
  type GameItem,
  type GameAction,
  type GameActiveState,
} from '../api/games';

type GameBulkAction = 'start' | 'stop' | 'restart' | 'backup';
type BulkState = 'pending' | 'running' | 'ok' | 'failed';
import { usePermission } from '../hooks/usePermission';
import { getSocket } from '../api/socket';
import { LogDrawer } from '../components/services/LogDrawer';
import { AddGameWizard } from '../components/games/AddGameWizard';
import { BackupDrawer } from '../components/games/BackupDrawer';
import { UploadDrawer } from '../components/games/UploadDrawer';
import { EditGameDialog } from '../components/games/EditGameDialog';
import { DeleteGameDialog } from '../components/games/DeleteGameDialog';
import { SavegameBrowseDialog } from '../components/games/SavegameBrowseDialog';
import { UpdateGameDialog } from '../components/games/UpdateGameDialog';
import { GameConfigDialog } from '../components/games/GameConfigDialog';
import { StatusTab } from '../components/games/StatusTab';
import { CrashHistoryDialog } from '../components/games/CrashHistoryDialog';
import { SessionHistoryDialog } from '../components/games/SessionHistoryDialog';
import { RconConsole } from '../components/games/RconConsole';
import { FileBrowserDrawer } from '../components/games/FileBrowserDrawer';
import { ChangelogDialog } from '../components/games/ChangelogDialog';
import { fetchCrashSummary } from '../api/gameCrashes';
import { useSnackbar } from '../context/SnackbarContext';

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtBytes(b: number): string {
  if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
  if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(0)} MB`;
  return `${(b / 1024).toFixed(0)} KB`;
}

// ── Bulk state chip ────────────────────────────────────────────────────────

function BulkStateChip({ state }: { state: BulkState }) {
  if (state === 'running') {
    return (
      <Chip
        label="running"
        color="info"
        size="small"
        icon={<CircularProgress size={10} color="inherit" sx={{ ml: 0.5 }} />}
      />
    );
  }
  if (state === 'ok') return <Chip label="done" color="success" size="small" />;
  if (state === 'failed') return <Chip label="failed" color="error" size="small" />;
  return <Chip label="queued" size="small" />;
}

// ── Status chip ────────────────────────────────────────────────────────────

function StatusChip({ active, installing, updating }: { active: GameActiveState; installing: boolean; updating: boolean }) {
  if (installing) return <Chip label="installing" color="info" size="small" />;
  if (updating) return <Chip label="updating" color="info" size="small" />;
  const map: Record<GameActiveState, { color: 'success' | 'error' | 'warning' | 'default'; label: string }> = {
    active:       { color: 'success', label: 'running' },
    inactive:     { color: 'default', label: 'stopped' },
    failed:       { color: 'error',   label: 'failed' },
    activating:   { color: 'warning', label: 'starting' },
    deactivating: { color: 'warning', label: 'stopping' },
    unknown:      { color: 'default', label: 'unknown' },
  };
  const { color, label } = map[active] ?? { color: 'default', label: active };
  return <Chip label={label} color={color} size="small" />;
}

// ── Action group ──────────────────────────────────────────────────────────

function ActionGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, display: 'block', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {children}
      </Stack>
    </Box>
  );
}

// ── Game tile ──────────────────────────────────────────────────────────────

interface GameTileProps {
  game: GameItem;
  crashCount24h: number;
  selected: boolean;
  bulkState: BulkState | undefined;
  isBulkRunning: boolean;
  onSelectToggle: () => void;
  onAction: (id: string, action: GameAction) => void;
  onLogs: (id: string) => void;
  onBackup: (id: string) => void;
  onUpload: (id: string) => void;
  onBrowse: (id: string) => void;
  onUpdate: (id: string) => void;
  onConfig: (id: string) => void;
  onCrashes: (id: string) => void;
  onSessions: (id: string) => void;
  onRcon: (id: string) => void;
  onFiles: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onChangelog: (id: string) => void;
  loading: boolean;
}

function GameTile({ game, crashCount24h, selected, bulkState, isBulkRunning, onSelectToggle, onAction, onLogs, onBackup, onUpload, onBrowse, onUpdate, onConfig, onCrashes, onSessions, onRcon, onFiles, onEdit, onDelete, onChangelog, loading }: GameTileProps) {
  const canControl = usePermission(`games.${game.id}.control`);
  const canBackup = usePermission(`games.${game.id}.backup`);
  const canUpload = usePermission(`games.${game.id}.upload`);
  const canRcon = usePermission(`games.${game.id}.rcon`);
  const canFiles = usePermission(`games.${game.id}.files.read`);
  const canManage = usePermission('games.manage');
  const isActive = game.status.active === 'active' || game.status.active === 'activating';
  const showSnackbar = useSnackbar();
  const [downloading, setDownloading] = useState(false);

  const handleSavegameDownload = async () => {
    setDownloading(true);
    try {
      const { token } = await fetchSavegameDownloadToken(game.id);
      triggerSavegameDownload(game.id, token);
    } catch {
      showSnackbar('Savegame-Download fehlgeschlagen', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const [imgSrc, setImgSrc] = useState<string | null>(
    game.thumbnail ? `/assets/${game.thumbnail}` : null,
  );
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgSrc(game.thumbnail ? `/assets/${game.thumbnail}` : null);
    setImgFailed(false);
  }, [game.thumbnail, game.id]);

  const handleImgError = () => {
    if (imgSrc?.startsWith('/assets/')) {
      setImgSrc(`/api/games/${game.id}/thumbnail`);
    } else {
      setImgFailed(true);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(selected && { outline: '2px solid', outlineColor: 'primary.main' }),
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {imgFailed || !imgSrc ? (
          <Box sx={{ minHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.disabledBackground' }}>
            <ImageNotSupportedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          </Box>
        ) : (
          <CardMedia
            component="img"
            image={imgSrc}
            alt={game.name}
            sx={{ minHeight: 150, objectFit: 'cover' }}
            onError={handleImgError}
          />
        )}
        <Checkbox
          size="small"
          checked={selected}
          onChange={onSelectToggle}
          onClick={(e) => e.stopPropagation()}
          disabled={isBulkRunning}
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            bgcolor: 'rgba(0,0,0,0.45)',
            borderRadius: 1,
            p: 0.25,
            color: 'white',
            '&.Mui-checked': { color: 'primary.light' },
            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
          }}
        />
        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
          {crashCount24h > 0 && (
            <Tooltip title={`${crashCount24h} crash${crashCount24h !== 1 ? 'es' : ''} (24h)`}>
              <Chip label={`${crashCount24h}x`} color="error" size="small"
                onClick={() => onCrashes(game.id)} sx={{ cursor: 'pointer' }} />
            </Tooltip>
          )}
          {game.auto_restart?.enabled && game.auto_restart.schedule && (
            <Tooltip title={`Auto-restart: ${game.auto_restart.schedule}`}>
              <Chip label="↻" size="small" color="warning" variant="outlined" sx={{ fontSize: 8, minWidth: 16 }} />
            </Tooltip>
          )}
          <StatusChip active={game.status.active} installing={game.installing} updating={game.updating} />
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            {game.name}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {game.ports.length > 0 && (
              <Tooltip title={`steam://connect/:${game.ports[0].port}`}>
                <IconButton
                  size="small"
                  href={`steam://connect/${window.location.hostname}:${game.ports[0].port}`}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {bulkState && <BulkStateChip state={bulkState} />}
          </Stack>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
          {game.provider}
        </Typography>

        {/* Resource mini-stats */}
        {game.status.active === 'active' && (
          <Box sx={{ mt: 1 }}>
            {game.status.cpu_percent !== undefined && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">CPU</Typography>
                <Typography variant="caption">{game.status.cpu_percent.toFixed(1)}%</Typography>
              </Stack>
            )}
            {game.status.ram_bytes !== undefined && (
              <>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">RAM</Typography>
                  <Typography variant="caption">{fmtBytes(game.status.ram_bytes)}</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (game.status.ram_bytes / 8_589_934_592) * 100)}
                  sx={{ height: 4, borderRadius: 1, bgcolor: 'action.disabledBackground' }}
                />
              </>
            )}
          </Box>
        )}

        {game.status.active === 'active' && (
          <Box sx={{ mt: 0.5 }}>
            <StatusTab gameId={game.id} systemdActive={game.status.active} provider={game.provider} />
          </Box>
        )}

        {game.install_error && (
          <Alert severity="error" sx={{ mt: 1, py: 0 }}>
            <Typography variant="caption">{game.install_error}</Typography>
          </Alert>
        )}
      </CardContent>

      {/* Primary control strip */}
      {canControl && !game.installing && (
        <>
          <Divider />
          <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.5 }}>
            {isActive ? (
              <Button fullWidth size="small" variant="outlined" color="warning"
                startIcon={<StopIcon />} onClick={() => onAction(game.id, 'stop')} disabled={loading}>
                Stop
              </Button>
            ) : (
              <Button fullWidth size="small" variant="outlined" color="success"
                startIcon={<PlayArrowIcon />} onClick={() => onAction(game.id, 'start')} disabled={loading}>
                Start
              </Button>
            )}
            <Button fullWidth size="small" variant="outlined"
              startIcon={<RestartAltIcon />} onClick={() => onAction(game.id, 'restart')} disabled={loading}>
              Restart
            </Button>
          </Stack>
        </>
      )}

      {/* Action groups */}
      <Divider />
      <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>

        {(canUpload || canBackup) && (
          <ActionGroup label="Savegames">
            {canUpload && (
              <Button size="small" variant="outlined" startIcon={<UploadFileIcon />}
                onClick={() => onUpload(game.id)} disabled={game.installing}>
                Upload
              </Button>
            )}
            {canBackup && (
              <Button size="small" variant="outlined" startIcon={<BackupIcon />}
                onClick={() => onBackup(game.id)} disabled={game.installing}>
                Backup
              </Button>
            )}
            {canBackup && (
              <Button size="small" variant="outlined" startIcon={<FolderOpenIcon />}
                onClick={() => onBrowse(game.id)} disabled={game.installing}>
                Browse
              </Button>
            )}
            {canBackup && (
              <Tooltip title="Download savegame archive">
                <span>
                  <Button size="small" variant="outlined"
                    startIcon={downloading ? <CircularProgress size={12} /> : <SaveAltIcon />}
                    onClick={() => void handleSavegameDownload()}
                    disabled={downloading || game.installing}>
                    Download
                  </Button>
                </span>
              </Tooltip>
            )}
          </ActionGroup>
        )}

        <ActionGroup label="Monitor">
          <Button size="small" variant="outlined" startIcon={<TerminalIcon />} onClick={() => onLogs(game.id)}>
            Logs
          </Button>
          <Button size="small" variant="outlined" startIcon={<PeopleIcon />} onClick={() => onSessions(game.id)}>
            Sessions
          </Button>
          {canRcon && game.rcon?.enabled && (
            <Button size="small" variant="outlined" startIcon={<DeveloperModeIcon />} onClick={() => onRcon(game.id)}>
              RCON
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<HistoryIcon />} onClick={() => onChangelog(game.id)}>
            Changelog
          </Button>
        </ActionGroup>

        {(canFiles || canManage) && (
          <ActionGroup label="Files &amp; Config">
            <Button size="small" variant="outlined" startIcon={<FolderIcon />} onClick={() => onFiles(game.id)}>
              File Browser
            </Button>
            {canManage && (
              <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={() => onConfig(game.id)}>
                Config Editor
              </Button>
            )}
          </ActionGroup>
        )}

        {canManage && (
          <ActionGroup label="Manage">
            <Tooltip title={game.update_available ? 'Update available' : 'Update via SteamCMD'}>
              <span>
                <Button size="small" variant="outlined"
                  color={game.update_available ? 'warning' : 'primary'}
                  startIcon={
                    <Badge color="warning" variant="dot" invisible={!game.update_available}>
                      <SystemUpdateAltIcon />
                    </Badge>
                  }
                  onClick={() => onUpdate(game.id)}
                  disabled={game.installing || game.updating}>
                  Update
                </Button>
              </span>
            </Tooltip>
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => onEdit(game.id)}>
              Edit
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(game.id)}>
              Delete
            </Button>
          </ActionGroup>
        )}

        {game.public_status && (
          <Box>
            <Button size="small" variant="outlined" color="info" startIcon={<OpenInNewIcon />}
              href={`/status/${game.id}`} target="_blank" rel="noopener noreferrer" component="a">
              Public Status
            </Button>
          </Box>
        )}

      </Stack>
    </Card>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export function Games() {
  const canRead = usePermission('games.read');
  const canManage = usePermission('games.manage');
  const qc = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['games'],
    queryFn: fetchGames,
    enabled: canRead,
    refetchInterval: 30_000,
  });

  // crash counts per game (24h window); updated by socket events
  const [crashCounts, setCrashCounts] = useState<Record<string, number>>({});

  const { data: crashSummaryData } = useQuery({
    queryKey: ['crash-summary'],
    queryFn: fetchCrashSummary,
    enabled: canRead,
    staleTime: 2 * 60_000,
    refetchInterval: 5 * 60_000,
  });

  const showSnackbar = useSnackbar();

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedGames, setSelectedGames] = useState<Set<string>>(new Set());
  const [bulkGameStates, setBulkGameStates] = useState<Record<string, BulkState>>({});
  const [bulkGameConfirm, setBulkGameConfirm] = useState<{ action: GameBulkAction; ids: string[] } | null>(null);
  const isBulkRunning = Object.keys(bulkGameStates).length > 0;

  const [logGame, setLogGame] = useState<{ id: string; name: string } | null>(null);
  const [backupGame, setBackupGame] = useState<{ id: string; name: string } | null>(null);
  const [uploadGame, setUploadGame] = useState<{ id: string; name: string } | null>(null);
  const [browseGame, setBrowseGame] = useState<{ id: string; name: string } | null>(null);
  const [updateGameItem, setUpdateGameItem] = useState<GameItem | null>(null);
  const [configGame, setConfigGame] = useState<GameItem | null>(null);
  const [crashGame, setCrashGame] = useState<{ id: string; name: string } | null>(null);
  const [sessionGame, setSessionGame] = useState<{ id: string; name: string } | null>(null);
  const [rconGame, setRconGame] = useState<GameItem | null>(null);
  const [filesGame, setFilesGame] = useState<{ id: string; name: string } | null>(null);
  const [editGame, setEditGame] = useState<GameItem | null>(null);
  const [deleteGameItem, setDeleteGameItem] = useState<GameItem | null>(null);
  const [changelogGame, setChangelogGame] = useState<{ id: string; name: string } | null>(null);

  // Seed crash counts from API result
  useEffect(() => {
    if (!crashSummaryData) return;
    const map: Record<string, number> = {};
    for (const item of crashSummaryData.summary) map[item.game_id] = item.count_24h;
    setCrashCounts(map);
  }, [crashSummaryData]);

  // Live updates via socket
  useEffect(() => {
    if (!canRead) return;
    const socket = getSocket();

    const onStatus = (items: Array<{ id: string; active: string; ram_bytes?: number; cpu_percent?: number; installing: boolean; updating: boolean }>) => {
      qc.setQueryData(['games'], (prev: { games: GameItem[] } | undefined) => {
        if (!prev) return prev;
        const byId = new Map(items.map((i) => [i.id, i]));
        return {
          games: prev.games.map((g) => {
            const snap = byId.get(g.id);
            if (!snap) return g;
            return {
              ...g,
              installing: snap.installing,
              updating: snap.updating,
              status: {
                ...g.status,
                active: snap.active as GameActiveState,
                ram_bytes: snap.ram_bytes,
                cpu_percent: snap.cpu_percent,
              },
            };
          }),
        };
      });
    };

    const onCrash = (data: { game_id: string; restart_count: number }) => {
      setCrashCounts((prev) => ({ ...prev, [data.game_id]: (prev[data.game_id] ?? 0) + 1 }));
    };

    socket.on('games:status', onStatus);
    socket.on('games:crash', onCrash);
    return () => {
      socket.off('games:status', onStatus);
      socket.off('games:crash', onCrash);
    };
  }, [canRead, qc]);

  if (!canRead) {
    return <Alert severity="error">You do not have permission to view games.</Alert>;
  }

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">Failed to load games.</Alert>;
  }

  const games = data?.games ?? [];

  const handleAction = async (id: string, action: GameAction) => {
    setActionLoading(`${id}-${action}`);
    try { await controlGame(id, action); } finally { setActionLoading(null); }
  };

  const handleLogs = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setLogGame({ id, name: game.name });
  };

  const handleBackup = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setBackupGame({ id, name: game.name });
  };

  const handleUpload = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setUploadGame({ id, name: game.name });
  };

  const handleBrowse = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setBrowseGame({ id, name: game.name });
  };

  const handleUpdate = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setUpdateGameItem(game);
  };

  const handleConfig = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setConfigGame(game);
  };

  const handleCrashes = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setCrashGame({ id, name: game.name });
  };

  const handleSessions = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setSessionGame({ id, name: game.name });
  };

  const handleRcon = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setRconGame(game);
  };

  const handleFiles = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setFilesGame({ id, name: game.name });
  };

  const handleEdit = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setEditGame(game);
  };

  const handleDelete = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setDeleteGameItem(game);
  };

  const handleChangelog = (id: string) => {
    const game = games.find((g) => g.id === id);
    if (game) setChangelogGame({ id, name: game.name });
  };

  const toggleSelectGame = (id: string) => {
    setSelectedGames((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const executeBulkGames = async () => {
    if (!bulkGameConfirm) return;
    const { action, ids } = bulkGameConfirm;
    setBulkGameConfirm(null);

    const initial: Record<string, BulkState> = {};
    for (const id of ids) initial[id] = 'pending';
    setBulkGameStates(initial);

    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      setBulkGameStates((prev) => ({ ...prev, [id]: 'running' }));
      try {
        if (action === 'backup') {
          await createBackup(id);
        } else {
          await controlGame(id, action);
        }
        setBulkGameStates((prev) => ({ ...prev, [id]: 'ok' }));
        ok++;
      } catch {
        setBulkGameStates((prev) => ({ ...prev, [id]: 'failed' }));
        fail++;
      }
    }

    setSelectedGames(new Set());
    const verb =
      action === 'start' ? 'started' :
      action === 'stop'  ? 'stopped' :
      action === 'restart' ? 'restarted' : 'backed up';
    showSnackbar(
      `${ok} of ${ids.length} ${verb}${fail > 0 ? `, ${fail} failed` : ''}`,
      fail > 0 ? 'warning' : 'success',
    );
    setTimeout(() => setBulkGameStates({}), 3_000);
    void qc.invalidateQueries({ queryKey: ['games'] });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={selectedGames.size > 0 ? 1 : 3}>
        <Typography variant="h5">Games</Typography>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}>
            Add Game
          </Button>
        )}
      </Stack>

      {selectedGames.size > 0 && !isBulkRunning && (
        <Paper sx={{ mb: 2, px: 2, py: 1, bgcolor: 'action.selected' }} elevation={0} variant="outlined">
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ mr: 1 }}>{selectedGames.size} selected</Typography>
            <Button size="small" variant="contained" color="success" onClick={() => setBulkGameConfirm({ action: 'start', ids: [...selectedGames] })}>Start</Button>
            <Button size="small" variant="contained" color="warning" onClick={() => setBulkGameConfirm({ action: 'stop', ids: [...selectedGames] })}>Stop</Button>
            <Button size="small" variant="contained" onClick={() => setBulkGameConfirm({ action: 'restart', ids: [...selectedGames] })}>Restart</Button>
            <Button size="small" variant="outlined" startIcon={<BackupIcon />} onClick={() => setBulkGameConfirm({ action: 'backup', ids: [...selectedGames] })}>Backup</Button>
            <Box flex={1} />
            <Button size="small" onClick={() => setSelectedGames(new Set())}>Clear</Button>
          </Stack>
        </Paper>
      )}

      {games.length === 0 ? (
        <Alert severity="info">
          No games configured. Add a game config to <code>/etc/webadmin/games/</code>.
        </Alert>
      ) : (
        <Grid container spacing={2} sx={{ width: '100%' }}>
          {games.map((game) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={game.id}>
              <GameTile
                game={game}
                crashCount24h={crashCounts[game.id] ?? 0}
                selected={selectedGames.has(game.id)}
                bulkState={bulkGameStates[game.id]}
                isBulkRunning={isBulkRunning}
                onSelectToggle={() => toggleSelectGame(game.id)}
                onAction={(id, action) => void handleAction(id, action)}
                onLogs={handleLogs}
                onBackup={handleBackup}
                onUpload={handleUpload}
                onBrowse={handleBrowse}
                onUpdate={handleUpdate}
                onConfig={handleConfig}
                onCrashes={handleCrashes}
                onSessions={handleSessions}
                onRcon={handleRcon}
                onFiles={handleFiles}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onChangelog={handleChangelog}
                loading={actionLoading?.startsWith(game.id) ?? false}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <LogDrawer
        serviceId={logGame?.id ?? null}
        serviceName={logGame?.name ?? ''}
        unit={logGame ? `gameserver@${logGame.id}.service` : null}
        onClose={() => setLogGame(null)}
      />

      <AddGameWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />

      <BackupDrawer
        gameId={backupGame?.id ?? null}
        gameName={backupGame?.name ?? ''}
        onClose={() => setBackupGame(null)}
      />

      <UploadDrawer
        gameId={uploadGame?.id ?? null}
        gameName={uploadGame?.name ?? ''}
        onClose={() => setUploadGame(null)}
      />

      <SavegameBrowseDialog
        open={browseGame !== null}
        gameId={browseGame?.id ?? null}
        gameName={browseGame?.name ?? ''}
        onClose={() => setBrowseGame(null)}
      />

      <UpdateGameDialog
        game={updateGameItem}
        onClose={() => setUpdateGameItem(null)}
      />

      <GameConfigDialog
        game={configGame}
        onClose={() => setConfigGame(null)}
      />

      <CrashHistoryDialog
        open={crashGame !== null}
        gameId={crashGame?.id ?? null}
        gameName={crashGame?.name ?? ''}
        onClose={() => setCrashGame(null)}
      />

      <ChangelogDialog
        open={changelogGame !== null}
        gameId={changelogGame?.id ?? null}
        gameName={changelogGame?.name ?? ''}
        onClose={() => setChangelogGame(null)}
      />

      <SessionHistoryDialog
        open={sessionGame !== null}
        gameId={sessionGame?.id ?? null}
        gameName={sessionGame?.name ?? ''}
        onClose={() => setSessionGame(null)}
      />

      <RconConsole
        game={rconGame}
        onClose={() => setRconGame(null)}
      />

      <FileBrowserDrawer
        open={filesGame !== null}
        gameId={filesGame?.id ?? ''}
        gameName={filesGame?.name ?? ''}
        onClose={() => setFilesGame(null)}
      />

      {editGame && (
        <EditGameDialog game={editGame} onClose={() => setEditGame(null)} />
      )}

      {deleteGameItem && (
        <DeleteGameDialog
          game={deleteGameItem}
          onClose={() => setDeleteGameItem(null)}
          onDeleted={() => setDeleteGameItem(null)}
        />
      )}

      <Dialog
        open={bulkGameConfirm !== null}
        onClose={() => setBulkGameConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textTransform: 'capitalize' }}>
          Bulk {bulkGameConfirm?.action}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {bulkGameConfirm?.action} {bulkGameConfirm?.ids.length} game(s):
          </Typography>
          <List dense disablePadding>
            {bulkGameConfirm?.ids.map((id) => (
              <ListItem key={id} disableGutters>
                <ListItemText primary={games.find((g) => g.id === id)?.name ?? id} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkGameConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => void executeBulkGames()}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
